import React from 'react'
import { useTranslation } from 'react-i18next'
import { BooleanParam, useQueryParam } from 'use-query-params'
import { useNavigate } from 'react-router-dom'

import { NarrowFulfilmentMethodInputType } from '@src/../__generated__/globalTypes'
import { BackToOutletListLink } from '@src/components/BackToOutletListLink/BackToOutletListLink'
import { convertToSlug } from '@src/utils/convertToSlug'
import { BottomSheetWrapper } from '@src/components/BottomSheetWrapper/BottomSheetWrapper'
import { OutletStatusTranslationText } from '@src/components/OutletStatus/OutletStatusTranslationText'
import { TextClamp } from '@src/components/Text/Clamp'
import { useOutletAvailabilityQuery } from '@src/hooks/sharedQueries/outletAvailabilityQuery/useOutletAvailabilityQuery'
import { useBreakpoint } from '@src/hooks/useBreakpoint'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { imageJitURL } from '@src/utils/imageJitURL'

import {
  Container,
  FirstRow,
  OutletDetailsContainer,
  DetailsButton,
  TitleBar,
  OpenStatus,
  Cuisines,
  RestaurantLogo,
  MinOrderText,
  FulfilmentPill,
  InnerDetails,
  DealsRow,
  OutletInfoRow,
  CoverImage,
  CuisinesAndMinOrderContainer,
  CompactHeaderTabContainer,
  OutletInfoOuterContainer,
  OutletTitle,
  HeaderContainer,
  HeaderLeftGroup,
  TitleAndPillContainer,
  TabletTitleAndPillContainer,
  InfoSVGStyled,
  CartSmallSVGStyled,
  StarSVGStyled,
} from './OutletOverview.styles'

import { OutletMenuDescription } from '../OutletDescription/OutletMenuDescription'
import { OutletDetails } from '../OutletDetails/OutletDetails'
import { HeaderTab } from '../OutletHeader/HeaderTab'
import { FulfilmentMethodTabMap } from '../OutletHeader/OutletHeader'
import { useOutletPageContext } from '../OutletPageContext'
import { useCheckoutRouter } from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { omit } from 'lodash'

export const OutletOverview: React.FC<{
  permanentCheckout: boolean
  isInStackedMenuView: boolean
  outletId: string
  outletDisplayName: string
}> = ({
  permanentCheckout,
  isInStackedMenuView,
  outletId,
  outletDisplayName,
}) => {
  const {
    isCollapsingDivVisible: compactStyle,
    outletFulfilment: {
      outlet,
      data: { currentFulfilment },
    },
  } = useOutletPageContext()
  const { displayPermanentCheckout } = useCheckoutRouter()
  const { urlPath } = useMarketplace()
  const navigate = useNavigate()

  const formatCurrency = useFormatCurrency(false)
  const [showDetails, setShowDetails] = useQueryParam(
    'showDetails',
    BooleanParam
  )
  const { t } = useTranslation('outletOverview')
  const { isMobile, isSmallerThanDesktop, isTablet, isLargeTablet } =
    useBreakpoint()

  const outletStatusTextResponse = useOutletAvailabilityQuery({
    outletId: outlet.id,
    fulfilmentMethod: currentFulfilment.narrowType,
    fulfilmentLocation: omit(currentFulfilment.location, 'address'),
  })

  const statusText = outletStatusTextResponse.data
    ? outletStatusTextResponse.data.outlet?.statusText
    : outletStatusTextResponse.previousData?.outlet?.statusText

  const { deliveryMinimumOrderValue, collectionMinimumOrderValue } = outlet

  const selectedNarrowFulfilment = currentFulfilment.narrowType

  const fulfilmentTabs = FulfilmentMethodTabMap[selectedNarrowFulfilment]

  const getMinimumOrderValue = () => {
    if (
      selectedNarrowFulfilment === NarrowFulfilmentMethodInputType.DELIVERY &&
      deliveryMinimumOrderValue
    ) {
      return (
        <>
          {formatCurrency(deliveryMinimumOrderValue)}{' '}
          {t('min_order', { val: null })}
        </>
      )
    }
    if (
      selectedNarrowFulfilment === NarrowFulfilmentMethodInputType.COLLECTION &&
      collectionMinimumOrderValue
    ) {
      return (
        <>
          {formatCurrency(collectionMinimumOrderValue)}{' '}
          {t('min_order', { val: null })}
        </>
      )
    }
    return t('no_min_order')
  }

  const MappedDeal: Record<
    NarrowFulfilmentMethodInputType,
    string | null | undefined
  > = {
    [NarrowFulfilmentMethodInputType.COLLECTION]:
      outlet.specialOfferFlags?.collectionDeal,
    [NarrowFulfilmentMethodInputType.DELIVERY]:
      outlet.specialOfferFlags?.deliveryDeal,
    [NarrowFulfilmentMethodInputType.TABLE]:
      outlet.specialOfferFlags?.tableDeal,
  }

  const isLongTitle = () => {
    if (compactStyle && isLargeTablet) {
      return outlet.displayName.length > 12
    }
    if (isMobile) {
      return outlet.displayName.length > 27
    }
    return outlet.displayName.length > 30
  }

  return (
    <>
      {showDetails && (
        <BottomSheetWrapper
          open={showDetails}
          onClose={() => setShowDetails(undefined)}
        >
          <OutletDetails />
        </BottomSheetWrapper>
      )}
      <Container compactStyle={compactStyle} isLongTitle={isLongTitle()}>
        <FirstRow compactStyle={compactStyle}>
          <OutletDetailsContainer compactStyle={compactStyle}>
            <InnerDetails>
              <HeaderContainer
                compactStyle={compactStyle}
                isLongTitle={isLongTitle()}
              >
                <HeaderLeftGroup>
                  <TabletTitleAndPillContainer compactStyle={compactStyle}>
                    <BackToOutletListLink
                      showContainer={false}
                      showText={false}
                      customOnClick={
                        isInStackedMenuView
                          ? () => {
                              window.scrollTo({ top: 0 })
                              navigate(
                                `/${urlPath}/${outletId}/${convertToSlug(
                                  outletDisplayName
                                )}/menu`,
                                { replace: true }
                              )
                            }
                          : undefined
                      }
                    />
                    {isTablet && statusText && (
                      <FulfilmentPill
                        status={statusText.orderButtonTranslation}
                      >
                        <OpenStatus status={statusText.orderButtonTranslation}>
                          <OutletStatusTranslationText
                            statusText={statusText}
                          />
                        </OpenStatus>
                      </FulfilmentPill>
                    )}
                  </TabletTitleAndPillContainer>
                  <TitleAndPillContainer
                    onClick={() => compactStyle && setShowDetails(true)}
                    compactStyle={compactStyle}
                    isLongTitle={isLongTitle()}
                  >
                    <TitleBar
                      isLongTitle={isLongTitle()}
                      compactStyle={compactStyle}
                    >
                      <OutletTitle
                        isLongTitle={isLongTitle()}
                        compactStyle={compactStyle}
                      >
                        <TextClamp clamp={2}>{outlet.displayName}</TextClamp>
                      </OutletTitle>
                    </TitleBar>
                    {!isTablet && statusText && (
                      <FulfilmentPill
                        status={statusText.orderButtonTranslation}
                      >
                        <OpenStatus status={statusText.orderButtonTranslation}>
                          <OutletStatusTranslationText
                            statusText={statusText}
                          />
                        </OpenStatus>
                      </FulfilmentPill>
                    )}
                  </TitleAndPillContainer>
                </HeaderLeftGroup>
                {isMobile && (
                  <RestaurantLogo
                    compactStyle={compactStyle}
                    permanentCheckout={displayPermanentCheckout}
                    role="img"
                    aria-label={t('logo_label', {
                      restaurant: outlet.displayName,
                    })}
                    imageUrl={imageJitURL(
                      outlet.outletLogoOverride || outlet.restaurant.image,
                      {
                        resize: {
                          width: 80,
                          height: 80,
                          fit: 'cover',
                        },
                        toFormat: 'jpeg',
                      }
                    )}
                  />
                )}
                {compactStyle && !isSmallerThanDesktop && (
                  <CompactHeaderTabContainer>
                    <HeaderTab
                      fulfilmentTabs={fulfilmentTabs}
                      permanentCheckout={permanentCheckout}
                    />
                  </CompactHeaderTabContainer>
                )}
              </HeaderContainer>
              {!compactStyle && (
                <OutletInfoOuterContainer>
                  {MappedDeal[selectedNarrowFulfilment] && (
                    <DealsRow
                      compactStyle={compactStyle}
                      isLongTitle={isLongTitle()}
                    >
                      <StarSVGStyled />
                      {MappedDeal[selectedNarrowFulfilment]}
                    </DealsRow>
                  )}
                  <OutletInfoRow>
                    <CuisinesAndMinOrderContainer>
                      {outlet.outletCuisines.length > 0 && (
                        <Cuisines compactStyle={compactStyle}>
                          {outlet.outletCuisines
                            .slice(0, 3)
                            .map(cuisine => cuisine.name)
                            .join(' • ')}
                        </Cuisines>
                      )}
                      <MinOrderText compactStyle={compactStyle}>
                        <CartSmallSVGStyled id="info" title="Info" />
                        {getMinimumOrderValue()}
                      </MinOrderText>
                    </CuisinesAndMinOrderContainer>
                    {!isMobile && (
                      <DetailsButton
                        compactStyle={compactStyle}
                        onClick={() => setShowDetails(true)}
                      >
                        <InfoSVGStyled id="info" title="Info" />
                        {t('more_info')}
                      </DetailsButton>
                    )}
                  </OutletInfoRow>
                  {outlet.description && (
                    <OutletMenuDescription
                      compactStyle={compactStyle}
                      description={outlet.description}
                    />
                  )}
                  {isMobile && (
                    <DetailsButton
                      compactStyle={compactStyle}
                      onClick={() => setShowDetails(true)}
                    >
                      <InfoSVGStyled id="info" title="Info" />
                      {t('more_info')}
                    </DetailsButton>
                  )}
                </OutletInfoOuterContainer>
              )}
            </InnerDetails>
          </OutletDetailsContainer>
          {!isSmallerThanDesktop && (
            <CoverImage
              compactStyle={compactStyle}
              role="img"
              aria-label={t('logo_label', {
                restaurant: outlet.displayName,
              })}
              imageUrl={imageJitURL(outlet.coverImage, {
                resize: {
                  width: 168,
                  height: 168,
                  fit: 'cover',
                },
                toFormat: 'jpeg',
              })}
            >
              <RestaurantLogo
                compactStyle={compactStyle}
                permanentCheckout={displayPermanentCheckout}
                role="img"
                aria-label={t('logo_label', {
                  restaurant: outlet.displayName,
                })}
                imageUrl={imageJitURL(
                  outlet.outletLogoOverride || outlet.restaurant.image,
                  {
                    resize: {
                      width: 168,
                      height: 168,
                      fit: 'cover',
                    },
                    toFormat: 'jpeg',
                  }
                )}
              />
            </CoverImage>
          )}
          {isSmallerThanDesktop && !isMobile && (
            <RestaurantLogo
              compactStyle={compactStyle}
              permanentCheckout={displayPermanentCheckout}
              role="img"
              aria-label={t('logo_label', {
                restaurant: outlet.displayName,
              })}
              imageUrl={imageJitURL(
                outlet.outletLogoOverride || outlet.restaurant.image,
                {
                  resize: {
                    width: 256,
                    height: 256,
                    fit: 'cover',
                  },
                  toFormat: 'jpeg',
                }
              )}
            />
          )}
        </FirstRow>
      </Container>
    </>
  )
}
